import { useState, useEffect, useRef } from 'react';
import { X, ArrowRight, CheckCircle2 } from 'lucide-react';
import {
  submitBetaSignup,
  submitBetaDetails,
  isValidEmail,
  JOB_ROLE_OPTIONS,
  type BetaJobRole,
  type BetaSignupSource,
} from '../../lib/betaSignup';

/**
 * Modale d'inscription à la bêta, ouverte depuis les CTA « Rejoindre la bêta ».
 *
 * Deux étapes, et l'ordre n'est pas négociable : l'email part au serveur dès la
 * première validation, avant même que la fiche soit remplie. Un visiteur qui
 * ferme la fenêtre en cours de route reste donc un lead joignable.
 *
 * Le composant reste monté en permanence (il rend `null` quand il est fermé) :
 * refermer puis rouvrir la modale ramène le visiteur là où il s'était arrêté,
 * sans lui redemander son email.
 */

type Step = 'email' | 'details' | 'done';

type Props = {
  open: boolean;
  onClose: () => void;
  source?: BetaSignupSource;
  /**
   * Pré-remplit le champ message à l’ouverture, pour capter le forfait qui a
   * déclenché la demande. Le champ reste librement éditable.
   */
  initialMessage?: string;
};

const INPUT_CLASS =
  'min-h-[48px] w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white ' +
  'placeholder-slate-500 outline-none transition-colors focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30';

const LABEL_CLASS = 'mb-1.5 block text-xs font-medium text-slate-400';

export default function BetaSignupModal({ open, onClose, source = 'landing', initialMessage = '' }: Props) {
  const [step, setStep] = useState<Step>('email');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [jobRole, setJobRole] = useState<BetaJobRole | ''>('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState(initialMessage);

  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  // Le message repart du pré-remplissage à chaque ouverture, sinon la saisie
  // faite pour un forfait resterait collée à l’ouverture suivante.
  useEffect(() => {
    if (open) setMessage(initialMessage);
  }, [open, initialMessage]);

  // Échap ferme, et le fond ne défile plus derrière la fenêtre.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  // Le premier champ de l'étape courante prend le focus : à l'ouverture comme
  // au passage à l'étape suivante.
  useEffect(() => {
    if (!open || step === 'done') return;
    const timer = window.setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open, step]);

  if (!open) return null;

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!isValidEmail(email)) {
      setError('Entrez une adresse email valide.');
      return;
    }
    setError('');
    setSubmitting(true);

    const result = await submitBetaSignup(email, source);
    setSubmitting(false);

    if (result.status === 'error') {
      setError(result.message);
      return;
    }
    setStep('details');
  };

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!firstName.trim() || !lastName.trim() || !company.trim() || !jobRole) {
      setError('Renseignez votre nom, votre société et votre fonction.');
      return;
    }
    setError('');
    setSubmitting(true);

    const result = await submitBetaDetails(
      email,
      { firstName, lastName, company, jobRole, phone, message },
      source
    );
    setSubmitting(false);

    if (result.status === 'error') {
      setError(result.message);
      return;
    }
    setStep('done');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6">
      {/* Fond : cliquer à côté ferme, comme l'attend n'importe quel visiteur. */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-slate-950/80 backdrop-blur-sm"
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="beta-modal-title"
        className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-800 bg-slate-900 p-6 shadow-2xl shadow-black/50 sm:p-8"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute right-4 top-4 text-slate-500 transition-colors hover:text-white"
        >
          <X size={18} />
        </button>

        {step === 'done' ? (
          <div className="py-4 text-center">
            <CheckCircle2 size={40} className="mx-auto mb-4 text-emerald-400" />
            <h2 id="beta-modal-title" className="mb-2 text-xl font-bold text-white">
              Demande enregistrée.
            </h2>
            <p className="mx-auto max-w-sm text-sm leading-relaxed text-slate-400">
              Vous recevez un email de confirmation dans quelques instants. Nous revenons vers
              vous rapidement avec les prochaines étapes.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 rounded-xl bg-amber-600 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-amber-500"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <h2 id="beta-modal-title" className="mb-1.5 pr-8 text-xl font-bold text-white">
              Rejoindre la bêta
            </h2>
            <p className="mb-6 text-sm leading-relaxed text-slate-400">
              {step === 'email'
                ? 'Accès réservé aux ESN, cabinets IT et équipes staffing souhaitant mieux exploiter leur vivier interne.'
                : 'Ces informations nous servent à préparer votre accès et à vous répondre utilement.'}
            </p>

            {step === 'email' ? (
              <form onSubmit={handleEmailSubmit} noValidate className="space-y-4">
                <div>
                  <label htmlFor="beta-email" className={LABEL_CLASS}>
                    Email professionnel
                  </label>
                  <input
                    ref={firstFieldRef}
                    id="beta-email"
                    type="email"
                    value={email}
                    onChange={e => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder="prenom.nom@societe.fr"
                    aria-invalid={error ? 'true' : 'false'}
                    className={INPUT_CLASS}
                  />
                </div>

                {error && <p className="text-sm font-medium text-red-300">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-6 font-bold text-white transition-colors hover:bg-amber-500 disabled:cursor-not-allowed disabled:bg-amber-800"
                >
                  {submitting ? 'Envoi…' : <>Continuer <ArrowRight size={16} /></>}
                </button>
              </form>
            ) : (
              <form onSubmit={handleDetailsSubmit} noValidate className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="beta-firstname" className={LABEL_CLASS}>Prénom</label>
                    <input
                      ref={firstFieldRef}
                      id="beta-firstname"
                      value={firstName}
                      onChange={e => { setFirstName(e.target.value); if (error) setError(''); }}
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label htmlFor="beta-lastname" className={LABEL_CLASS}>Nom</label>
                    <input
                      id="beta-lastname"
                      value={lastName}
                      onChange={e => { setLastName(e.target.value); if (error) setError(''); }}
                      className={INPUT_CLASS}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="beta-company" className={LABEL_CLASS}>Société</label>
                  <input
                    id="beta-company"
                    value={company}
                    onChange={e => { setCompany(e.target.value); if (error) setError(''); }}
                    className={INPUT_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="beta-role" className={LABEL_CLASS}>Votre fonction</label>
                  <select
                    id="beta-role"
                    value={jobRole}
                    onChange={e => { setJobRole(e.target.value as BetaJobRole); if (error) setError(''); }}
                    className={INPUT_CLASS}
                  >
                    <option value="">Sélectionnez…</option>
                    {JOB_ROLE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="beta-phone" className={LABEL_CLASS}>
                    Téléphone <span className="text-slate-600">(facultatif)</span>
                  </label>
                  <input
                    id="beta-phone"
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>

                <div>
                  <label htmlFor="beta-message" className={LABEL_CLASS}>
                    Votre besoin <span className="text-slate-600">(facultatif)</span>
                  </label>
                  <textarea
                    id="beta-message"
                    rows={3}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Combien de profils gérez-vous ? Qu'est-ce qui vous coûte le plus de temps aujourd'hui ?"
                    className={`${INPUT_CLASS} min-h-[88px] resize-none py-3`}
                  />
                </div>

                {error && <p className="text-sm font-medium text-red-300">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-amber-600 px-6 font-bold text-white transition-colors hover:bg-amber-500 disabled:cursor-not-allowed disabled:bg-amber-800"
                >
                  {submitting ? 'Envoi…' : <>Envoyer ma demande <ArrowRight size={16} /></>}
                </button>

                <p className="text-center text-xs text-slate-500">
                  Votre email est déjà enregistré : vous pouvez fermer cette fenêtre, nous
                  reviendrons vers vous.
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
